<template>
  <div>
    <!-- Área de exibição da foto -->
    <v-container class="face">
      <v-col class="captura">
        <canvas ref="canvas" style="display: none;"></canvas>
        <img v-if="photo" :src="photo" width="200" height="200" alt="Foto Capturada" />
        <img v-else-if="!photo && captura" :src="captura" width="200" height="200" alt="Foto Capturada" />
        <img v-else src="@/img/avatar.jpg" width="200" height="200" alt="Avatar Padrão" />
      </v-col>
    </v-container>

    <!-- Modal profissional para captura facial -->
    <v-dialog v-model="showCamera" max-width="420" persistent>
      <v-card class="professional-dialog">
        <v-card-title class="professional-dialog-header">
          <v-icon left color="white" small>mdi-face-recognition</v-icon>
          Reconhecimento Facial
        </v-card-title>
        
        <v-card-text class="pa-4">
          <div class="camera-section">
            <div class="camera-container">
              <video ref="video" autoplay muted class="camera-video"></video>
              <canvas ref="debugCanvas" class="debug-canvas"></canvas>
              <div class="face-overlay">
                <div class="face-circle" :class="{ 'recognizing': recognizing }"></div>
                <div v-if="recognizing" class="countdown">{{ countdown }}</div>
              </div>
            </div>
            
            <div class="status-section">
              <div class="status-indicator" :class="getStatusClass()">
                <v-icon small class="mr-1">{{ getStatusIcon() }}</v-icon>
                <span class="status-text">{{ getStatusText() }}</span>
              </div>
              
              <v-progress-linear 
                v-if="capturing || recognizing" 
                :indeterminate="!recognizing"
                :value="recognizing ? ((3 - countdown) / 3) * 100 : 0"
                color="grey darken-2" 
                height="3"
                class="mt-2"
              ></v-progress-linear>
            </div>
          </div>
          
          <div v-if="confidenceLevel !== null && recognizing" class="confidence-panel">
            <div class="confidence-content">
              <div class="confidence-main">
                <div class="confidence-value">{{ (confidenceLevel * 100).toFixed(1) }}%</div>
                <div class="confidence-label">Confiança</div>
              </div>
              
              <v-progress-linear 
                :value="confidenceLevel * 100" 
                :color="confidenceColor" 
                height="6"
                rounded
                class="mt-1"
              ></v-progress-linear>
              
              <div class="confidence-status" :class="confidenceColor + '--text'">
                {{ confidenceText }}
              </div>
            </div>
          </div>
        </v-card-text>

        <v-card-actions class="pa-3">
          <v-spacer></v-spacer>
          <v-btn 
            v-if="!hasExistingPhoto" 
            color="grey darken-3" 
            dark
            @click="capture" 
            :disabled="capturing || recognizing"
          >
            <v-icon left small>mdi-camera-iris</v-icon>
            Cadastrar Foto
          </v-btn>
          <v-btn 
            text 
            color="grey darken-1" 
            @click="closeCamera" 
            :disabled="recognizing"
          >
            <v-icon left small>mdi-close</v-icon>
            Cancelar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Bus from '@/barramento'
import axios from 'axios'
import { baseApiUrl } from '@/global'
import * as faceapi from 'face-api.js'

export default {
  name: 'FaceApi',
  props:['captura'],
  
  computed: {
    confidenceColor() {
      if (this.confidenceLevel === null) return 'grey'
      if (this.confidenceLevel > 0.7) return 'success'
      if (this.confidenceLevel > 0.5) return 'info'
      if (this.confidenceLevel > 0.3) return 'warning'
      return 'error'
    },
    confidenceText() {
      if (this.confidenceLevel === null) return ''
      if (this.confidenceLevel === 0) return 'Posicione o rosto no círculo verde'
      if (this.confidenceLevel > 0.7) return 'Perfeito - Contando...'
      if (this.confidenceLevel > 0.5) return 'Bom - Mantenha a posição'
      if (this.confidenceLevel > 0.3) return 'Regular - Ajuste a posição'
      return 'Baixo - Centralize no círculo'
    }
  },
  data() {
    return {
      photo: null,
      showCamera: false,
      capturing: false,
      stream: null,
      currentUser: null,
      hasExistingPhoto: false,
      recognizing: false,
      countdown: 0,
      countdownInterval: null,
      modelsLoaded: false,
      referenceDescriptor: null,
      confidenceLevel: null,
      originalSimilarity: null,
      pixelDistance: null,
      detectionScore: null
    }
  },

  methods: {
    openCamera(user) {
      console.log('📱 Abrindo câmera mobile para:', user.nome)
      this.currentUser = user
      this.showCamera = true
      
      // Reset completo antes de verificar
      this.hasExistingPhoto = false
      this.referenceDescriptor = null
      this.recognizing = false
      this.countdown = 0
      
      // Verificar foto existente
      this.checkExistingPhoto(user).then(() => {
        console.log('Status foto existente:', this.hasExistingPhoto)
      })
      
      // Aguardar o DOM atualizar e iniciar câmera
      this.$nextTick(() => {
        setTimeout(() => {
          console.log('🔄 Iniciando câmera após DOM ready...')
          this.startCamera()
        }, 500) // Aumentar delay para mobile
      })
    },

    async startCamera() {
      try {
        console.log('🎥 Iniciando câmera mobile...')
        
        // Configurações específicas para mobile
        const constraints = {
          video: {
            facingMode: 'user',
            width: { ideal: 640, max: 1280 },
            height: { ideal: 480, max: 720 }
          },
          audio: false
        }
        
        console.log('📱 Solicitando permissão da câmera...')
        const stream = await navigator.mediaDevices.getUserMedia(constraints)
        
        console.log('✅ Stream obtido:', stream.getVideoTracks().length, 'tracks')
        this.stream = stream
        
        if (this.$refs.video) {
          this.$refs.video.srcObject = stream
          
          // Aguardar o vídeo carregar
          await new Promise((resolve) => {
            this.$refs.video.onloadedmetadata = () => {
              console.log('📹 Vídeo carregado:', this.$refs.video.videoWidth, 'x', this.$refs.video.videoHeight)
              resolve()
            }
          })
          
          console.log('🎯 Câmera conectada e funcionando')
        }
        
        console.log('Câmera iniciada. Foto existente:', this.hasExistingPhoto)
        if (this.hasExistingPhoto) {
          console.log('🚀 Iniciando reconhecimento automático')
          this.startRecognition()
        } else {
          console.log('📷 Aguardando cadastro de foto')
        }
      } catch (err) {
        console.error('❌ Erro câmera:', err)
        let errorMsg = 'Erro ao acessar câmera: '
        if (err.name === 'NotAllowedError') {
          errorMsg += 'Permissão negada. Verifique as configurações do navegador.'
        } else if (err.name === 'NotFoundError') {
          errorMsg += 'Câmera não encontrada.'
        } else {
          errorMsg += err.message
        }
        alert(errorMsg)
      }
    },

    async checkExistingPhoto(user) {
      try {
        const timestamp = Date.now()
        await axios.get(`${baseApiUrl}/uploadFile/captura_${user.usuario}.png?t=${timestamp}`)
        this.hasExistingPhoto = true
        console.log('✅ Foto encontrada para:', user.usuario)
      } catch (err) {
        this.hasExistingPhoto = false
        console.log('❌ Nenhuma foto para:', user.usuario)
      }
    },

    startRecognition() {
      console.log('Iniciando reconhecimento...')
      this.recognizing = true
      this.countdown = 3
      
      // Verificar confiança a cada 500ms
      this.countdownInterval = setInterval(async () => {
        await this.checkConfidenceAndCountdown()
      }, 500)
    },

    async checkConfidenceAndCountdown() {
      try {
        // Capturar foto atual para verificar confiança
        const canvas = this.$refs.canvas
        const context = canvas.getContext('2d')
        const video = this.$refs.video

        canvas.width = 240
        canvas.height = 240
        context.drawImage(video, 0, 0, canvas.width, canvas.height)

        const currentPhoto = canvas.toDataURL('image/png')
        
        // Verificar confiança sem fazer match completo
        await this.checkConfidenceOnly(currentPhoto)
        
        // Só contar se score > 0.7
        if (this.detectionScore && this.detectionScore > 0.7) {
          this.countdown -= 0.5
          console.log('Countdown:', this.countdown, 'Score:', (this.detectionScore * 100).toFixed(0) + '%')
          
          if (this.countdown <= 0) {
            clearInterval(this.countdownInterval)
            this.countdownInterval = null
            this.recognizeFace()
          }
        } else {
          // Reset countdown se score baixar
          if (this.countdown < 3) {
            this.countdown = 3
            console.log('Score baixo, resetando countdown')
          }
        }
      } catch (err) {
        console.error('Erro na verificação de confiança:', err)
      }
    },

    async checkConfidenceOnly(currentPhoto) {
      try {
        const img = await this.createImageElement(currentPhoto)
        const detection = await faceapi.detectSingleFace(img, new faceapi.TinyFaceDetectorOptions({ inputSize: 160 }))
        
        // Desenhar debug
        this.drawDetectionDebug(detection)
        
        if (!detection) {
          this.detectionScore = 0
          this.confidenceLevel = 0
          return
        }
        
        // Verificar se está no círculo (RIGOROSO)
        const box = detection.box
        const centerX = box.x + box.width / 2
        const centerY = box.y + box.height / 2
        const imgCenterX = img.width / 2
        const imgCenterY = img.height / 2
        const radius = 42 // Proporcional ao círculo de 140px
        
        const distance = Math.sqrt(
          Math.pow(centerX - imgCenterX, 2) + 
          Math.pow(centerY - imgCenterY, 2)
        )
        
        if (distance > radius) {
          console.log('🚫 Fora do círculo - Distância:', distance.toFixed(0))
          this.detectionScore = 0
          this.confidenceLevel = 0
          return
        }
        
        // Score como critério final
        this.detectionScore = detection.score
        this.confidenceLevel = detection.score
        
        console.log('🔍 Score:', (detection.score * 100).toFixed(0) + '% | Dist:', distance.toFixed(0))
        
      } catch (err) {
        this.detectionScore = 0
        this.confidenceLevel = 0
      }
    },

    async recognizeFace() {
      console.log('🏆 RECONHECIMENTO APROVADO - Score > 70% por 3 segundos!')
      
      try {
        // Capturar foto atual para substituir a referência
        const fotoAtual = this.captureCurrentFrame()
        
        this.$toasted.success('Rosto reconhecido com sucesso!')
        console.log('Emitindo evento face-recognized')
        this.$emit('face-recognized', fotoAtual)
        
        setTimeout(() => {
          this.closeCamera()
        }, 1000)
      } catch (error) {
        console.error('Erro no reconhecimento:', error)
        let errorMessage = 'Erro no reconhecimento facial'
        
        if (error.response && error.response.data) {
          errorMessage = error.response.data
        } else if (error.message) {
          errorMessage = error.message
        }
        
        this.$toasted.error(errorMessage)
        this.closeCamera()
      }
    },

    async loadFaceApiModels() {
      try {
        console.log('Carregando modelos leves...')
        const MODEL_URL = '/models'
        
        // Apenas o essencial para velocidade
        await faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL)
        await faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_URL)
        
        this.modelsLoaded = true
        console.log('⚡ Modelos leves carregados!')
      } catch (err) {
        console.error('Erro ao carregar modelos:', err)
        this.modelsLoaded = false
      }
    },

    async compareFaces(currentPhoto) {
      console.log('🚀 === INICIANDO COMPARAÇÃO FINAL ===')
      
      // Verificar se rosto está no círculo
      const faceInCircle = await this.checkFaceInCircle(currentPhoto)
      if (!faceInCircle) {
        console.log('❌ FALHOU: Rosto fora do círculo')
        return false
      }
      
      console.log('✅ Rosto no círculo, executando comparação...')
      const result = await this.simpleComparison(currentPhoto)
      console.log('🏁 RESULTADO FINAL:', result ? '✅ APROVADO' : '❌ REJEITADO')
      return result
    },

    async checkFaceInCircle(photo) {
      try {
        const img = await this.createImageElement(photo)
        const detection = await faceapi.detectSingleFace(img, new faceapi.TinyFaceDetectorOptions({ inputSize: 160 }))
        
        // Desenhar pontos de detecção para debug
        this.drawDetectionDebug(detection)
        
        if (!detection) return false
        
        // Verificar qualidade da detecção pelo score
        if (detection.score < 0.6) {
          console.log('Score muito baixo:', detection.score.toFixed(2))
          return false
        }
        
        const box = detection.box
        const centerX = box.x + box.width / 2
        const centerY = box.y + box.height / 2
        
        // Verificar se o centro do rosto está na área central (círculo)
        const imgCenterX = img.width / 2
        const imgCenterY = img.height / 2
        const radius = 42 // Proporcional ao círculo de 140px
        
        const distance = Math.sqrt(
          Math.pow(centerX - imgCenterX, 2) + 
          Math.pow(centerY - imgCenterY, 2)
        )
        
        return distance <= radius
      } catch (err) {
        return false
      }
    },

    async simpleComparison(currentPhoto) {
      console.log('Executando comparação facial com descritores...')
      try {
        const referencePhoto = await this.loadReferencePhoto()
        const similarity = await this.calculateFaceDescriptorSimilarity(currentPhoto, referencePhoto)
        
        // Armazenar dados para exibição
        this.originalSimilarity = similarity
        this.pixelDistance = similarity
        this.confidenceLevel = similarity
        
        console.log('🔍 Similaridade:', (similarity * 100).toFixed(1) + '%')
        console.log('🔍 Threshold: 35%')
        console.log('🔍 Match:', similarity > 0.35 ? '✅ SIM' : '❌ NÃO')
        
        return similarity > 0.35 // Threshold mais baixo
      } catch (err) {
        console.error('Erro comparação facial:', err)
        this.confidenceLevel = 0
        this.originalSimilarity = null
        this.pixelDistance = null
        return false
      }
    },

    async loadReferencePhoto() {
      return new Promise((resolve, reject) => {
        const img = new Image()
        img.crossOrigin = 'anonymous'
        img.onload = () => {
          const canvas = document.createElement('canvas')
          const ctx = canvas.getContext('2d')
          canvas.width = 400
          canvas.height = 400
          ctx.drawImage(img, 0, 0, 400, 400)
          resolve(canvas.toDataURL('image/png'))
        }
        img.onerror = reject
        img.src = `${baseApiUrl}/uploadFile/captura_${this.currentUser.usuario}.png`
      })
    },

    async calculateFaceDescriptorSimilarity(photo1, photo2) {
      try {
        // Cache do descritor de referência
        if (!this.referenceDescriptor) {
          const img2 = await this.createImageElement(photo2)
          const detection2 = await faceapi
            .detectSingleFace(img2, new faceapi.TinyFaceDetectorOptions({ inputSize: 160 }))
            .withFaceDescriptor()
          
          if (!detection2) {
            return 0
          }
          this.referenceDescriptor = detection2.descriptor
        }
        
        // Processar foto atual (super rápido)
        const img1 = await this.createImageElement(photo1)
        const detection1 = await faceapi
          .detectSingleFace(img1, new faceapi.TinyFaceDetectorOptions({ inputSize: 160 }))
          .withFaceDescriptor()
        
        if (!detection1) {
          return 0
        }
        
        const distance = faceapi.euclideanDistance(detection1.descriptor, this.referenceDescriptor)
        const similarity = Math.max(0, 1 - (distance / 0.5))
        
        return similarity
        
      } catch (err) {
        return 0
      }
    },

    getImageData(base64) {
      return new Promise((resolve) => {
        const img = new Image()
        img.onload = () => {
          const canvas = document.createElement('canvas')
          const ctx = canvas.getContext('2d')
          canvas.width = 400
          canvas.height = 400
          ctx.drawImage(img, 0, 0, 400, 400)
          resolve(ctx.getImageData(0, 0, 400, 400))
        }
        img.src = base64
      })
    },



    createImageElement(src) {
      return new Promise((resolve, reject) => {
        const img = new Image()
        img.crossOrigin = 'anonymous'
        img.onload = () => resolve(img)
        img.onerror = reject
        img.src = src
      })
    },

    drawDetectionDebug(detection) {
      const debugCanvas = this.$refs.debugCanvas
      if (!debugCanvas) return
      
      const ctx = debugCanvas.getContext('2d')
      debugCanvas.width = 240
      debugCanvas.height = 240
      
      // Limpar canvas
      ctx.clearRect(0, 0, 240, 240)
      
      if (detection) {
        const box = detection.box
        
        // Desenhar apenas ponto central
        const centerX = box.x + box.width / 2
        const centerY = box.y + box.height / 2
        
        ctx.fillStyle = '#ff0000'
        ctx.beginPath()
        ctx.arc(centerX, centerY, 3, 0, 2 * Math.PI)
        ctx.fill()
        
        // Mostrar score com cor baseada na qualidade
        const scoreColor = detection.score > 0.8 ? '#00ff00' : detection.score > 0.6 ? '#ffff00' : '#ff0000'
        ctx.fillStyle = scoreColor
        ctx.font = '14px Arial'
        ctx.fillText(`${(detection.score * 100).toFixed(0)}%`, centerX + 10, centerY)
      }
    },

    captureCurrentFrame() {
      const canvas = this.$refs.canvas
      const context = canvas.getContext('2d')
      const video = this.$refs.video

      const size = 240
      canvas.width = size
      canvas.height = size
      
      context.clearRect(0, 0, size, size)
      context.save()
      
      // Espelhar para corrigir orientação
      context.scale(-1, 1)
      context.translate(-size, 0)
      
      // Centralizar e cortar vídeo
      const videoAspect = video.videoWidth / video.videoHeight
      let drawWidth, drawHeight, offsetX, offsetY
      
      if (videoAspect > 1) {
        drawHeight = size
        drawWidth = size * videoAspect
        offsetX = -(drawWidth - size) / 2
        offsetY = 0
      } else {
        drawWidth = size
        drawHeight = size / videoAspect
        offsetX = 0
        offsetY = -(drawHeight - size) / 2
      }
      
      context.drawImage(video, offsetX, offsetY, drawWidth, drawHeight)
      context.restore()
      
      return canvas.toDataURL('image/jpeg', 0.9)
    },

    async capture() {
      if (!this.currentUser) return
      
      // OBRIGATÓRIO: Verificar se rosto está no círculo antes de capturar
      const canvas = this.$refs.canvas
      const context = canvas.getContext('2d')
      const video = this.$refs.video
      
      canvas.width = 240
      canvas.height = 240
      context.drawImage(video, 0, 0, canvas.width, canvas.height)
      const testPhoto = canvas.toDataURL('image/png')
      
      const faceInCircle = await this.checkFaceInCircle(testPhoto)
      if (!faceInCircle) {
        this.$toasted.error('Posicione seu rosto dentro do círculo verde!')
        return
      }
      
      // Reutilizar canvas já configurado para captura final

      // Definir tamanho quadrado para melhor qualidade
      const size = 512
      canvas.width = size
      canvas.height = size
      
      // Limpar canvas
      context.clearRect(0, 0, size, size)
      
      // Salvar contexto para transformações
      context.save()
      
      // Espelhar horizontalmente para corrigir orientação
      context.scale(-1, 1)
      context.translate(-size, 0)
      
      // Desenhar vídeo centralizado e cortado
      const videoAspect = video.videoWidth / video.videoHeight
      let drawWidth, drawHeight, offsetX, offsetY
      
      if (videoAspect > 1) {
        // Vídeo mais largo que alto
        drawHeight = size
        drawWidth = size * videoAspect
        offsetX = -(drawWidth - size) / 2
        offsetY = 0
      } else {
        // Vídeo mais alto que largo
        drawWidth = size
        drawHeight = size / videoAspect
        offsetX = 0
        offsetY = -(drawHeight - size) / 2
      }
      
      context.drawImage(video, offsetX, offsetY, drawWidth, drawHeight)
      
      // Restaurar contexto
      context.restore()
      
      // Converter para base64 com qualidade alta
      this.photo = canvas.toDataURL('image/jpeg', 0.95)
      const fileName = `captura_${this.currentUser.usuario}.png`
      
      this.uploadPhoto(this.photo, fileName, this.currentUser.id)
      
      this.capturing = false
      this.$toasted.success('Foto cadastrada com sucesso!')
      
      // Atualizar status de foto existente
      this.hasExistingPhoto = true
      
      // Limpar cache do descritor para nova foto
      this.referenceDescriptor = null
      
      // Emitir evento para o componente pai
      console.log('Emitindo evento photo-captured')
      this.$emit('photo-captured')
      
      // Alternativa via barramento
      Bus.$emit('photo-captured')
      
      setTimeout(() => {
        this.closeCamera()
      }, 2000)
    },

    closeCamera() {
      console.log('Fechando câmera e resetando estados')
      
      if (this.countdownInterval) {
        clearInterval(this.countdownInterval)
        this.countdownInterval = null
      }
      
      if (this.stream) {
        this.stream.getTracks().forEach(track => track.stop())
        this.stream = null
      }
      
      // Reset completo de todos os estados
      this.showCamera = false
      this.capturing = false
      this.recognizing = false
      this.countdown = 0
      this.hasExistingPhoto = false
      this.currentUser = null
      this.referenceDescriptor = null
      this.confidenceLevel = null
      this.originalSimilarity = null
      this.pixelDistance = null
      this.detectionScore = null
      
      // Forçar reatividade
      this.$forceUpdate()
    },

    async uploadPhoto(base64, fileName, id) {
      try{
       const blob = this.base64ToBlob(base64);
       
      //   // Criar FormData para envio
       const formData = new FormData();

       formData.append('file', blob, fileName);

      //   // Enviar via Axios
       const response = await axios.put(`${baseApiUrl}/uploads/${id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

       console.log('Foto enviada com sucesso:', response.data);
     } catch (err) {console.log(err)
     console.error('Erro ao enviar a foto:', err);
   }
 },

 base64ToBlob(base64) {
  const byteString = atob(base64.split(',')[1]);
  const mimeString = base64.split(',')[0].split(':')[1].split(';')[0];
  const arrayBuffer = new ArrayBuffer(byteString.length);
  const intArray = new Uint8Array(arrayBuffer);

  for (let i = 0; i < byteString.length; i++) {
    intArray[i] = byteString.charCodeAt(i);
  }

  return new Blob([arrayBuffer], { type: mimeString });
},

    getStatusClass() {
      if (this.capturing) return 'status-capturing'
      if (this.recognizing) return 'status-recognizing'
      if (this.hasExistingPhoto) return 'status-ready'
      return 'status-waiting'
    },

    getStatusIcon() {
      if (this.capturing) return 'mdi-camera'
      if (this.recognizing) return 'mdi-face-recognition'
      if (this.hasExistingPhoto) return 'mdi-check-circle'
      return 'mdi-account-plus'
    },

    getStatusText() {
      if (this.capturing) return 'Capturando imagem...'
      if (this.recognizing) return 'Reconhecendo rosto...'
      if (this.hasExistingPhoto) return 'Foto encontrada! Aguarde o reconhecimento...'
      return 'Nenhuma foto cadastrada. Posicione seu rosto e clique em Cadastrar'
    }
},

async mounted() {
  console.log('FaceApi montado, carregando modelos...')
  
  // Verificar suporte à câmera
  if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
    console.error('❌ getUserMedia não suportado neste navegador')
    return
  }
  
  console.log('✅ getUserMedia suportado')
  await this.loadFaceApiModels()
  
  Bus.$on('face', (user) => {
    console.log('📡 Evento face recebido:', user)
    this.openCamera(user)
  })
},

beforeDestroy() {
  this.closeCamera()
  Bus.$off('face')
}
}
</script>

<style lang="css" scoped>
.face {
  width: 100%;
  max-width: 240px;
  height: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  margin: 0 auto;
}

img {
  border: 1px solid #e0e0e0;
  margin-top: 20px;
  border-radius: 4px;
  max-width: 100%;
  height: auto;
  object-fit: cover;
}

.professional-dialog {
  border-radius: 4px;
}

@media (max-width: 600px) {
  .professional-dialog {
    margin: 8px;
    max-width: calc(100vw - 16px) !important;
  }
  
  .camera-video {
    max-width: 280px;
    height: 280px;
  }
  
  .face-circle {
    width: 120px;
    height: 120px;
  }
}

.professional-dialog-header {
  background: #424242;
  color: white;
  font-weight: 500;
  font-size: 16px;
  padding: 16px 24px;
}

.camera-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.camera-container {
  position: relative;
  display: inline-block;
  border-radius: 4px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.camera-video {
  width: 100%;
  max-width: 240px;
  height: 240px;
  border-radius: 4px;
  border: 2px solid #424242;
  display: block;
  object-fit: cover;
}

.debug-canvas {
  position: absolute;
  top: 0;
  left: 0;
  width: 240px;
  height: 240px;
  pointer-events: none;
  z-index: 2;
}

.face-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.face-circle {
  width: 140px;
  height: 140px;
  border: 3px solid #757575;
  border-radius: 50%;
  background: rgba(117, 117, 117, 0.1);
  animation: pulse 2s infinite;
  transition: all 0.3s ease;
}

.face-circle.recognizing {
  border-color: #424242;
  background: rgba(66, 66, 66, 0.2);
  animation: recognize 1s infinite;
}

.countdown {
  position: absolute;
  font-size: 3rem;
  font-weight: 500;
  color: #424242;
  text-shadow: 2px 2px 4px rgba(255,255,255,0.8);
}

.status-section {
  width: 100%;
  max-width: 240px;
}

.status-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 12px;
  border-radius: 4px;
  border: 1px solid #e0e0e0;
  background: #fafafa;
}

.status-text {
  font-size: 13px;
  color: #424242;
  font-weight: 400;
}

.status-capturing {
  border-color: #2b5672;
  background: #eaf0f4;
}

.status-recognizing {
  border-color: #ff9800;
  background: #fff3e0;
}

.status-ready {
  border-color: #4caf50;
  background: #e8f5e8;
}

.status-waiting {
  border-color: #757575;
  background: #f5f5f5;
}

.confidence-panel {
  margin-top: 12px;
  padding: 12px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  background: #fafafa;
}



.confidence-content {
  text-align: center;
}

.confidence-main {
  margin-bottom: 8px;
}

.confidence-value {
  font-size: 20px;
  font-weight: 500;
  color: #424242;
  line-height: 1;
}

.confidence-label {
  font-size: 11px;
  color: #757575;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-top: 2px;
}

.confidence-status {
  font-size: 12px;
  font-weight: 500;
  margin-top: 6px;
}

@keyframes pulse {
  0% {
    transform: scale(1);
    opacity: 0.8;
  }
  50% {
    transform: scale(1.02);
    opacity: 0.6;
  }
  100% {
    transform: scale(1);
    opacity: 0.8;
  }
}

@keyframes recognize {
  0% {
    transform: scale(1);
    border-width: 3px;
  }
  50% {
    transform: scale(1.05);
    border-width: 4px;
  }
  100% {
    transform: scale(1);
    border-width: 3px;
  }
}
</style>
